import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class RapidClickGuardService implements OnDestroy {
  private readonly minClickIntervalMs = 700;
  private readonly guardedSelector = [
    'button',
    '[role="button"]',
    'a[mat-button]',
    'a[mat-raised-button]',
    'a[mat-stroked-button]',
    'a[mat-flat-button]',
    'a[mat-icon-button]',
  ].join(',');
  private readonly allowRapidClickSelector = '[data-allow-rapid-click]';
  private lastClickedElement: Element | null = null;
  private lastClickAt = 0;
  private removeListener?: () => void;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone
  ) {
    this.ngZone.runOutsideAngular(() => {
      const clickHandler = (event: MouseEvent) => this.handleClick(event);
      this.document.addEventListener('click', clickHandler, true);
      this.removeListener = () =>
        this.document.removeEventListener('click', clickHandler, true);
    });
  }

  ngOnDestroy(): void {
    this.removeListener?.();
  }

  private handleClick(event: MouseEvent): void {
    if (event.defaultPrevented || event.button !== 0) {
      return;
    }

    const target = event.target as Element | null;
    if (!target?.closest) {
      return;
    }

    const clickable = target.closest(this.guardedSelector);
    if (!clickable || clickable.closest(this.allowRapidClickSelector)) {
      return;
    }

    if (this.isDisabled(clickable)) {
      return;
    }

    const now = Date.now();
    if (
      this.lastClickedElement === clickable &&
      now - this.lastClickAt < this.minClickIntervalMs
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      return;
    }

    this.lastClickedElement = clickable;
    this.lastClickAt = now;
  }

  private isDisabled(element: Element): boolean {
    return (
      element.hasAttribute('disabled') ||
      element.getAttribute('aria-disabled') === 'true' ||
      element.classList.contains('disabled')
    );
  }
}
